import React, { useEffect, useState } from "react";
import { Modal, Typography, Radio, Button, Flex } from "antd";
import { MinusOutlined, PlusOutlined } from "@ant-design/icons";
import { MenuItem } from "../models/MenuItem";
import { Flavour } from "../models/Flavour";

const { Title, Text, Paragraph } = Typography;
const BRAND = "#00AF66";

interface Props {
    visible: boolean;
    item: MenuItem | null;
    onClose: () => void;
    onAddToCart: (item: MenuItem, quantity: number, flavour: Flavour | null, unitPrice: number) => void;
}

const MenuItemModal: React.FC<Props> = ({ visible, item, onClose, onAddToCart }) => {
    const [quantity, setQuantity] = useState(1);
    const [selectedFlavour, setSelectedFlavour] = useState<Flavour | null>(null);

    useEffect(() => {
        if (item) {
            setQuantity(1);
            setSelectedFlavour(item.flavours?.[0] ?? null);
        }
    }, [item]);

    if (!item) return null;

    const unitPrice = selectedFlavour?.price ?? item.flavours?.[0]?.price ?? 0;
    const total = (unitPrice / 100) * quantity;

    const handleAdd = () => {
        onAddToCart(item, quantity, selectedFlavour, unitPrice);
        onClose();
    };

    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            centered
            width="92%"
            style={{ maxWidth: 480 }}
            destroyOnClose
        >
            <div style={{ marginBottom: 16 }}>
                {item.image_url ? (
                    <img
                        src={item.image_url}
                        alt={item.name}
                        style={{ width: "100%", height: 200, objectFit: "cover", borderRadius: 12 }}
                    />
                ) : (
                    <div style={{
                        width: "100%", height: 200, borderRadius: 12,
                        background: "#E6F7EF", display: "flex", alignItems: "center", justifyContent: "center",
                        fontSize: 64,
                    }}>
                        🍗
                    </div>
                )}
            </div>

            <Title level={4} style={{ marginBottom: 4 }}>{item.name}</Title>
            {item.description && (
                <Paragraph type="secondary" style={{ marginBottom: 16 }}>{item.description}</Paragraph>
            )}

            {item.flavours && item.flavours.length > 1 && (
                <div style={{ marginBottom: 20 }}>
                    <Text strong style={{ display: "block", marginBottom: 8 }}>Choose an option</Text>
                    <Radio.Group
                        value={selectedFlavour?.id}
                        onChange={(e) => {
                            const f = item.flavours!.find(fl => fl.id === e.target.value) ?? null;
                            setSelectedFlavour(f);
                        }}
                        style={{ width: "100%" }}
                    >
                        <Flex vertical gap={8} style={{ width: "100%" }}>
                            {item.flavours.map(f => (
                                <Radio key={f.id} value={f.id} style={{ width: "100%" }}>
                                    <Flex justify="space-between" style={{ width: "100%" }}>
                                        <Text>{f.name}</Text>
                                        <Text strong>R {(f.price / 100).toFixed(2)}</Text>
                                    </Flex>
                                </Radio>
                            ))}
                        </Flex>
                    </Radio.Group>
                </div>
            )}

            <Flex align="center" justify="space-between" style={{ marginBottom: 24 }}>
                <Text strong>Quantity</Text>
                <Flex align="center" gap={12}>
                    <Button
                        shape="circle"
                        icon={<MinusOutlined />}
                        onClick={() => setQuantity(q => Math.max(1, q - 1))}
                        disabled={quantity <= 1}
                    />
                    <Text strong style={{ minWidth: 24, textAlign: "center" }}>{quantity}</Text>
                    <Button
                        shape="circle"
                        icon={<PlusOutlined />}
                        onClick={() => setQuantity(q => q + 1)}
                    />
                </Flex>
            </Flex>

            <Button
                type="primary"
                size="large"
                block
                onClick={handleAdd}
                style={{ background: BRAND, borderColor: BRAND }}
            >
                Add to Cart — R {total.toFixed(2)}
            </Button>
        </Modal>
    );
};

export default MenuItemModal;